import {
  ReactElement, ReactNode, useEffect, useMemo, useState,
} from 'react';
import { t } from 'i18next';
import Skeleton from 'react-loading-skeleton';

import {
  Users02Icon,
  Building02Icon,
  CurrencyDollarCircleIcon,
  SnowflakeIcon,
  RefrigerantGasIcon,
} from 'icons';
import { EvaporatorIcon } from '~/icons/sidebar';
import { formatNumberWithFractionDigits } from '~/helpers/thousandFormatNumber';
import { getUserProfile } from '~/helpers/userProfile';

import { MaintenanceFilters } from '../types';
import { InfoTooltip } from './InfoTooltip';
import { ConversionFactorTooltip } from './ConversionFactorTooltip';
import { MaintenanceSummary, getMaintenanceSummary } from '../helpers/api';
import {
  Container,
  IconBox,
  Item,
  Label,
  MainValue,
  TitleRow,
  UnitValue,
  ValueWrapper,
} from './styles';

interface BigNumbersProps {
  filters: MaintenanceFilters;
}

type FormattedValue = { number: string; unit: string };

type CardConfig = {
  key: keyof MaintenanceSummary;
  testId: string;
  icon: ReactNode;
  labelKey: string;
  format: (value: number) => FormattedValue;
  tooltip?: ReactNode;
};

type CompactParts = { value: string; suffix: string };

function toCompactParts(value: number): CompactParts {
  const abs = Math.abs(value);
  if (abs >= 1e9) {
    return {
      value: formatNumberWithFractionDigits(value / 1e9, { minimum: 2, maximum: 2 }),
      suffix: 'B',
    };
  }
  if (abs >= 1e6) {
    return {
      value: formatNumberWithFractionDigits(value / 1e6, { minimum: 1, maximum: 1 }),
      suffix: 'M',
    };
  }
  if (abs >= 1e3) {
    return {
      value: formatNumberWithFractionDigits(value / 1e3, { minimum: 1, maximum: 1 }),
      suffix: 'K',
    };
  }
  return {
    value: formatNumberWithFractionDigits(value, { minimum: 0, maximum: 1 }),
    suffix: '',
  };
}

function formatInteger(value: number): FormattedValue {
  return {
    number: formatNumberWithFractionDigits(value, { minimum: 0, maximum: 0 }),
    unit: '',
  };
}

function formatCurrency(value: number): FormattedValue {
  const { value: formatted, suffix } = toCompactParts(value);
  return { number: `R$ ${formatted}`, unit: suffix };
}

function formatTR(value: number): FormattedValue {
  const { value: formatted, suffix } = toCompactParts(value);
  return { number: formatted, unit: suffix ? `${suffix} TR` : 'TR' };
}

function formatFluid(value: number): FormattedValue {
  if (Math.abs(value) < 1e3) {
    return {
      number: formatNumberWithFractionDigits(value, { minimum: 0, maximum: 1 }),
      unit: 'kg',
    };
  }
  const tons = value / 1e3;
  const { value: formatted, suffix } = toCompactParts(tons);
  return { number: formatted, unit: suffix ? `${suffix} t` : 't' };
}

const cardsConfig: CardConfig[] = [
  {
    key: 'CLIENTS_COUNT',
    testId: 'clients',
    icon: <Users02Icon size="20" />,
    labelKey: 'clientes',
    format: formatInteger,
  },
  {
    key: 'UNITS_COUNT',
    testId: 'units',
    icon: <Building02Icon size="20" />,
    labelKey: 'unidades',
    format: formatInteger,
  },
  {
    key: 'MACHINES_COUNT',
    testId: 'machines',
    icon: <EvaporatorIcon size={20} />,
    labelKey: 'maquinas',
    format: formatInteger,
  },
  {
    key: 'HVAC_ASSET_VALUE',
    testId: 'hvac',
    icon: <CurrencyDollarCircleIcon size="20" />,
    labelKey: 'patrimonioHvac',
    format: formatCurrency,
    tooltip: <ConversionFactorTooltip factorKey="fatorConversaoPatrimonioHvac" />,
  },
  {
    key: 'MACHINES_COOLING_CAPACITY',
    testId: 'capacity',
    icon: <SnowflakeIcon size="20" />,
    labelKey: 'capacidadeRefrigeracao',
    format: formatTR,
  },
  {
    key: 'REFRIGERANT_FLUID',
    testId: 'fluid',
    icon: <RefrigerantGasIcon size="20" />,
    labelKey: 'fluidoRefrigerante',
    format: formatFluid,
    tooltip: <ConversionFactorTooltip factorKey="fatorConversaoFluido" />,
  },
];

export function BigNumbers({ filters }: Readonly<BigNumbersProps>): ReactElement {
  const [profile] = useState(getUserProfile);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<MaintenanceSummary | undefined>();

  const hasMultipleClients = !!(profile.manageAllClients || profile.manageMultipleClients);
  const visibleCards = useMemo(
    () => cardsConfig.filter((card) => card.key !== 'CLIENTS_COUNT' || hasMultipleClients),
    [hasMultipleClients],
  );

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      setLoading(true);
      const data = await getMaintenanceSummary(filters);
      if (cancelled) return;
      setSummary(data);
      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [filters]);

  const hasOnlyClientData = !loading && !!summary && [
    summary.UNITS_COUNT,
    summary.MACHINES_COUNT,
    summary.HVAC_ASSET_VALUE,
    summary.MACHINES_COOLING_CAPACITY,
    summary.REFRIGERANT_FLUID,
  ].every((value) => !value);

  if (hasOnlyClientData) return <></>;

  return (
    <Container $columns={visibleCards.length}>
      {visibleCards.map((card) => {
        const rawValue = summary?.[card.key];
        const showValue = !loading && rawValue !== undefined;
        const formatted = showValue ? card.format(rawValue) : undefined;

        return (
          <Item key={card.key} data-testid={`maintenance-kpi-${card.testId}`}>
            <TitleRow>
              {showValue && formatted ? (
                <>
                  <IconBox>{card.icon}</IconBox>
                  <ValueWrapper>
                    <MainValue>{formatted.number}</MainValue>
                    {formatted.unit && <UnitValue>{formatted.unit}</UnitValue>}
                  </ValueWrapper>
                </>
              ) : (
                <>
                  <Skeleton width={32} height={32} borderRadius={8} inline />
                  <Skeleton width={110} height={32} borderRadius={8} inline />
                </>
              )}
            </TitleRow>
            {showValue ? (
              <Label>
                {t(card.labelKey)}
                {card.tooltip && (
                  <InfoTooltip id={`maintenance-kpi-${card.testId}`}>
                    {card.tooltip}
                  </InfoTooltip>
                )}
              </Label>
            ) : (
              <Skeleton width={80} height={14} borderRadius={100} />
            )}
          </Item>
        );
      })}
    </Container>
  );
}
